/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { actions } from '../../actions';
import { services } from '../../services';
import { Record, RentalData, Web3Data } from '../../types/nft/create.rental';
// Shadcn UI components
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

import { Input } from '../../components/ui/input';

import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/use-toast';
import { createRental, sendTransactionHash } from '../../services/transactionApi';
import { createAdminNotification } from '../../services/notificationService';
import { web3 } from '../../web3';

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const CreateRental: React.FC<{ record: Record }> = ({ record }) => {
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [rentalContractInstance, setRentalContractInstance] = useState<any>(null);
  const [data, setData] = useState<RentalData>({
    assetAddress: record.assetAddress,
    pricePerDay: '',
    duration: 0,
    durationUnit: 0,
    allowpayrent: 0,
    rentalDescription: '',
    beds: 0,
    baths: 0,
    sqft: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [isRental, setIsRental] = useState(false);

  const initializeWeb3 = useCallback(async () => {
    try {
      const web3Data = await services.getWeb3();
      if (web3Data) {
        setWeb3Data(web3Data as Web3Data);
      }
    } catch (error) {
      console.error('Web3 initialization failed:', error);
      setError('Failed to initialize Web3. Please check your wallet connection.');
    }
  }, []);

  const initializeContract = useCallback(async () => {
    try {
      const instance = await actions.getRentalPoolContract();
      setRentalContractInstance(instance);
      const rentalCheck = await instance.methods.isPropertyListed(record.assetAddress).call();
      setIsRental(Boolean(rentalCheck));
    } catch (error) {
      console.error('Rental contract initialization failed:', error);
      setError('Failed to initialize rental contract. Please try again later.');
    }
  }, [record.assetAddress]);

  useEffect(() => {
    initializeWeb3();
    initializeContract();
  }, [initializeWeb3, initializeContract]);

  const handleOpenDialog = async () => {
    if (!web3Data || !web3Data.accounts || web3Data.accounts.length === 0) {
      setError('Web3 data or accounts not available. Please connect your wallet.');

      return;
    }

    const owner = await rentalContractInstance.methods.owner().call();

    if (owner !== web3Data.accounts[0]) {
      toast({
        description: "You haven't logged in with the owner account.",

        variant: 'destructive',
      });

      return;
    }
    setShowDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setError(null);
  };

  const handleCreateRental = async (): Promise<void> => {
    if (!web3Data || !web3Data.accounts || web3Data.accounts.length === 0) {
      setError('Web3 data or accounts not available. Please connect your wallet.');
      return;
    }

    setLoading(true);
    setError(null);

    if (!data.pricePerDay) {
      setError('Price per day is required');
      setLoading(false);
      return;
    }

    let price;
    try {
      //@ts-ignore
      price = web3.utils.toWei(data.pricePerDay, 'ether');
      if (
        !rentalContractInstance ||
        !data.duration ||
        data.durationUnit === null ||
        !data.rentalDescription ||
        !data.beds ||
        !data.baths ||
        !data.sqft
      ) {
        throw new Error('Missing required rental data');
      }

      // Check if property is enabled/registered on the rental contract (isPropertyListed)
      try {
        const isRegisteredOnRental = await rentalContractInstance.methods
          .isPropertyListed(data.assetAddress)
          .call();

        if (!isRegisteredOnRental) {
          toast({
            description: 'Property not yet enabled on rental contract. Please approve in MetaMask to enable it...',
            variant: 'default',
          });

          await rentalContractInstance.methods
            .setPropertyAddress(data.assetAddress)
            .send({
              from: web3Data.accounts[0],
              gas: 500000,
              gasPrice: web3.utils.toWei('20', 'gwei'),
            });

          toast({
            description: 'Property enabled on rental contract! Now submitting rental listing...',
            variant: 'default',
          });
        }
      } catch (enableErr: any) {
        console.error('Failed to enable property on rental contract:', enableErr);
        let errMsg = enableErr?.data?.message || enableErr?.message || 'Failed to enable property on rental contract';
        errMsg = errMsg.replace(/: 0x[0-9a-fA-F]+/g, '').trim();
        toast({
          description: `Cannot enable property: ${errMsg}`,
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Estimate gas with safe fallback cap (BSC node cap is 16,777,216)
      let gasLimit = 3000000;
      try {
        const estimatedGas = await rentalContractInstance.methods
          .listPropertyForRent(
            data.assetAddress,
            data.duration,
            data.durationUnit,
            price,
            data.allowpayrent
          )
          .estimateGas({ from: web3Data.accounts[0] });
        gasLimit = Math.min(Math.floor(Number(estimatedGas) * 1.3), 3000000);
      } catch (gasErr) {
        console.warn('Gas estimation failed, using safe gas limit 3000000:', gasErr);
        gasLimit = 3000000;
      }

      await rentalContractInstance.methods
        .listPropertyForRent(
          data.assetAddress,
          data.duration,
          data.durationUnit,
          price,
          data.allowpayrent
        )
        .send({
          from: web3Data.accounts[0],
          gas: gasLimit,
          gasPrice: web3.utils.toWei('20', 'gwei'),
        })
        .on('transactionHash', async (hash) => {
          console.log('Rental transaction submitted:', hash);

          try {
            console.log('Sending transaction hash to backend...');
            const apiResponse = await sendTransactionHash(hash);
            console.log('Transaction hash recorded successfully');
          } catch (apiError) {
            console.error('Failed to record transaction hash:', apiError);
          }
        })
        .on('receipt', async (receipt: any) => {
          console.log('Rental transaction mined successfully:', receipt.transactionHash);

          toast({
            description: 'Rental created successfully on blockchain',
            variant: 'default',
          });
          createAdminNotification({
            type: 'transaction',
            title: 'Rental created',
            message: `Rental listing created for asset ${data.assetAddress?.slice(0, 10)}...`,
            priority: 'medium',
          });

          // Wait for additional confirmations before updating metadata
          // This helps ensure the transaction is stable on the blockchain
          console.log('Waiting for transaction stability before saving metadata...');
          await delay(2000);

          // Save structured rental metadata to backend for better data management
          const metadataPayload = {
            txHash: receipt.transactionHash,
            rentalDescription: data.rentalDescription,
            beds: data.beds,
            baths: data.baths,
            sqft: data.sqft,
            // Additional structured data for better querying and management
            assetAddress: data.assetAddress,
            duration: data.duration,
            durationUnit: data.durationUnit,
            pricePerDay: data.pricePerDay,
            allowpayrent: data.allowpayrent,
          };

          try {
            console.log('Saving rental metadata to backend...');
            await createRental(metadataPayload);
            console.log('Rental metadata saved successfully');
            toast({
              description: 'Rental metadata saved successfully',
              variant: 'default',
            });
          } catch (error) {
            console.error('Failed to save rental metadata:', error);
            toast({
              description: 'Rental created but metadata save failed',
              variant: 'destructive',
            });
          }
        })

        .on('error', async (error: any) => {
          console.error('Rental transaction failed:', error);

          // Record failed rental attempt with structured error data
          const failedRentalData = {
            assetAddress: data.assetAddress,
            rentalDescription: data.rentalDescription,
            beds: data.beds,
            baths: data.baths,
            sqft: data.sqft,
            duration: data.duration,
            durationUnit: data.durationUnit,
            pricePerDay: data.pricePerDay,
            allowpayrent: data.allowpayrent,
            listedBy: web3Data.accounts[0],
            txHash: null,
            status: 'FAILED',
            error: error.message || 'Transaction failed',
            errorDetails: error.toString(),
          };

          try {
            console.log('Recording failed rental attempt...');
            await createRental(failedRentalData);
            console.log('Failed rental attempt recorded');
          } catch (recordError) {
            console.error('Failed to record rental error:', recordError);
          }

          toast({
            description: `Rental transaction failed: ${error.message || 'Unknown error'}`,
            variant: 'destructive',
          });
        });
      setShowDialog(false);
    } catch (error: any) {
      setError(error.message || 'Error creating rental');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant='default'
        onClick={handleOpenDialog}
        disabled={loading || !isRental}
        className='bg-primary hover:bg-primary/90 text-primary-foreground'
        style={{ color: '#095D52' }}
      >
        {loading ? (
          <>
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            Processing
          </>
        ) : (
          'Rent Now'
        )}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Create Rental</DialogTitle>
            <DialogDescription>Set up rental details for this asset</DialogDescription>
          </DialogHeader>

          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='pricePerDay'>Rental Amount</Label>
              <Input
                id='pricePerDay'
                placeholder=''
                value={data.pricePerDay}
                onChange={(e) => setData({ ...data, pricePerDay: e.target.value })}
              />
            </div>

            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='duration'>Rent Duration</Label>
              <div className='flex items-center gap-2'>
                <Input
                  id='duration'
                  type='number'
                  className='w-1/2 h-10'
                  value={data.duration}
                  onChange={(e) => setData({ ...data, duration: parseInt(e.target.value) })}
                />
                <Select
                  value={data.durationUnit.toString()}
                  onValueChange={(value) => setData({ ...data, durationUnit: parseInt(value) })}
                >
                  <SelectTrigger className='w-1/2 h-10 px-3 py-2'>
                    <SelectValue placeholder='Unit' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='0'>Days</SelectItem>
                    <SelectItem value='1'>Months</SelectItem>
                    <SelectItem value='2'>Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='duration'>Rental Distributor</Label>

              <div className='flex items-center gap-2'>
                <Select
                  value={data.allowpayrent.toString()}
                  onValueChange={(value) => setData({ ...data, allowpayrent: parseInt(value) })}
                >
                  <SelectTrigger className='w-1/2 h-10 px-3 py-2'>
                    <SelectValue placeholder='Role' />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value='1'>Tenant</SelectItem>

                    <SelectItem value='0'>Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='rentalDescription'>Rental Description</Label>

              <Input
                id='rentalDescription'
                placeholder='Enter rental description'
                value={data.rentalDescription}
                onChange={(e) => setData({ ...data, rentalDescription: e.target.value })}
              />
            </div>

            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='beds'>Beds</Label>
              <Input
                id='beds'
                type='number'
                value={data.beds}
                onChange={(e) => setData({ ...data, beds: parseInt(e.target.value) })}
              />
            </div>
            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='baths'>Baths</Label>
              <Input
                id='baths'
                type='number'
                value={data.baths}
                onChange={(e) => setData({ ...data, baths: parseInt(e.target.value) })}
              />
            </div>
            <div className='grid grid-cols-1 gap-2'>
              <Label htmlFor='sqft'>Square Feet</Label>
              <Input
                id='sqft'
                type='number'
                value={data.sqft}
                onChange={(e) => setData({ ...data, sqft: parseInt(e.target.value) })}
              />
            </div>

            {/* {error && (
              <div className="text-destructive text-sm mt-2">{error}</div>
            )} */}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleCreateRental} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateRental;
